import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const CreditLimitIncreaseEstimator = () => {
  const content = article.find((item) => item.id === 8);
  const [cards, setCards] = useState([
    {
      id: Date.now(),
      name: "Primary Credit Card",
      limit: "5000",
      balance: "1500",
      apr: "18.99",
      issuer: "Chase",
      income: "6000",
      expenses: "4500",
      creditScore: "720",
      paymentHistory: "24",
      desiredIncrease: "2000",
      percentIncrease: "40",
      lastIncreaseMonths: "12",
      openAccounts: "3",
      accountAge: "24",
      monthlySpending: "1200",
      utilizationHistory: "25",
      employmentLength: "36",
      housingPayment: "1500",
      requestedAmountType: "percentage", // 'amount' or 'percentage'
    },
  ]);

  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const resultsRef = useRef();

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = cards.some(card => 
      card.limit && card.balance && card.income
    );
    if (hasValidData) {
      setTimeout(() => {
        calculateEstimates();
      }, 100);
    }
  }, [cards]);

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: `Credit Card ${cards.length + 1}`,
      limit: "3000",
      balance: "800",
      apr: "22.99",
      issuer: "Bank of America",
      income: "6000",
      expenses: "4500",
      creditScore: "680",
      paymentHistory: "18",
      desiredIncrease: "1500",
      percentIncrease: "50",
      lastIncreaseMonths: "18",
      openAccounts: "4",
      accountAge: "18",
      monthlySpending: "800",
      utilizationHistory: "30",
      employmentLength: "24",
      housingPayment: "1200",
      requestedAmountType: "percentage",
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const handleCardChange = (id, field, value) => {
    const newCards = cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    );
    setCards(newCards);
  };

  const resetCalculator = () => {
    setCards([{
      id: Date.now(),
      name: "Primary Credit Card",
      limit: "5000",
      balance: "1500",
      apr: "18.99",
      issuer: "Chase",
      income: "6000",
      expenses: "4500",
      creditScore: "720",
      paymentHistory: "24",
      desiredIncrease: "2000",
      percentIncrease: "40",
      lastIncreaseMonths: "12",
      openAccounts: "3",
      accountAge: "24",
      monthlySpending: "1200",
      utilizationHistory: "25",
      employmentLength: "36",
      housingPayment: "1500",
      requestedAmountType: "percentage",
    }]);
    setResults(null);
    setActiveCardIndex(0);
    setScenarioComparison(null);
  };

  const calculateEstimates = () => {
    const validCards = cards.filter(card => 
      card.limit && card.balance && card.income
    );

    if (validCards.length === 0) {
      alert("Please fill in required fields for at least one card (Limit, Balance, and Income)");
      return;
    }

    const calcResults = validCards.map(card => {
      const limit = parseFloat(card.limit) || 0;
      const balance = parseFloat(card.balance) || 0;
      const income = parseFloat(card.income) || 0;
      const expenses = parseFloat(card.expenses) || 0;
      const creditScore = parseInt(card.creditScore) || 0;
      const paymentHistory = parseInt(card.paymentHistory) || 0;
      const lastIncrease = parseInt(card.lastIncreaseMonths) || 0;
      const openAccounts = parseInt(card.openAccounts) || 0;
      const accountAge = parseInt(card.accountAge) || 0;
      const monthlySpending = parseFloat(card.monthlySpending) || 0;
      const utilizationHistory = parseFloat(card.utilizationHistory) || 0;
      const employmentLength = parseInt(card.employmentLength) || 0;
      const housingPayment = parseFloat(card.housingPayment) || 0;

      // Calculate requested increase
      let requestedIncrease = parseFloat(card.desiredIncrease) || 0;
      const percentIncrease = parseFloat(card.percentIncrease) || 0;
      
      if (card.requestedAmountType === "percentage" && percentIncrease > 0) {
        requestedIncrease = (limit * percentIncrease) / 100;
      }

      // Calculate key metrics
      const currentUtilization = limit > 0 ? (balance / limit) * 100 : 0;
      const disposableIncome = income - expenses;
      const dtiRatio = expenses > 0 ? (expenses / income) * 100 : 0;
      const housingRatio = income > 0 ? (housingPayment / income) * 100 : 0;

      // Advanced approval algorithm
      let approvalScore = 0;
      let maxRecommendedIncrease = 0;

      // Factor 1: Credit Utilization (30 points)
      if (currentUtilization < 10) approvalScore += 30;
      else if (currentUtilization < 30) approvalScore += 25;
      else if (currentUtilization < 50) approvalScore += 15;
      else if (currentUtilization < 70) approvalScore += 5;

      // Factor 2: Credit Score (25 points)
      if (creditScore >= 800) approvalScore += 25;
      else if (creditScore >= 750) approvalScore += 20;
      else if (creditScore >= 700) approvalScore += 15;
      else if (creditScore >= 650) approvalScore += 10;
      else if (creditScore >= 600) approvalScore += 5;

      // Factor 3: Payment History (20 points)
      if (paymentHistory >= 24) approvalScore += 20;
      else if (paymentHistory >= 12) approvalScore += 15;
      else if (paymentHistory >= 6) approvalScore += 10;
      else if (paymentHistory >= 3) approvalScore += 5;

      // Factor 4: Time Since Last Increase (15 points)
      if (lastIncrease >= 12) approvalScore += 15;
      else if (lastIncrease >= 6) approvalScore += 10;
      else if (lastIncrease >= 3) approvalScore += 5;

      // Factor 5: Income and DTI (10 points)
      if (dtiRatio < 30) approvalScore += 10;
      else if (dtiRatio < 40) approvalScore += 7;
      else if (dtiRatio < 50) approvalScore += 3;

      // Calculate max recommended increase
      const incomeBasedMax = income * 0.2; // 20% of income
      const utilizationBasedMax = balance > 0 ? (balance / 0.3) - limit : income * 0.15;
      maxRecommendedIncrease = Math.min(incomeBasedMax, utilizationBasedMax, limit * 2);

      // Calculate new utilization
      const newUtilization = (balance / (limit + requestedIncrease)) * 100;
      const newUtilizationRecommended = (balance / (limit + maxRecommendedIncrease)) * 100;

      // Determine approval likelihood
      let likelihood = "Medium";
      let likelihoodScore = "60%";
      
      if (approvalScore >= 80) {
        likelihood = "Very High";
        likelihoodScore = "85-100%";
      } else if (approvalScore >= 65) {
        likelihood = "High";
        likelihoodScore = "70-84%";
      } else if (approvalScore >= 50) {
        likelihood = "Medium";
        likelihoodScore = "50-69%";
      } else if (approvalScore >= 35) {
        likelihood = "Low";
        likelihoodScore = "30-49%";
      } else {
        likelihood = "Very Low";
        likelihoodScore = "0-29%";
      }

      // Generate recommendations
      const recommendations = [];
      if (currentUtilization > 30) {
        recommendations.push({
          type: "critical",
          text: `Pay down balance from $${balance.toFixed(2)} to $${(limit * 0.3).toFixed(2)} to improve utilization below 30%`
        });
      }
      if (lastIncrease < 6) {
        recommendations.push({
          type: "warning",
          text: `Wait ${6 - lastIncrease} more month(s) since last increase (most issuers prefer 6+ months)`
        });
      }
      if (creditScore < 700) {
        recommendations.push({
          type: "warning",
          text: "Improve credit score to 700+ for better approval chances"
        });
      }
      if (dtiRatio > 40) {
        recommendations.push({
          type: "warning",
          text: "Reduce monthly expenses to lower debt-to-income ratio below 40%"
        });
      }
      
      recommendations.push({
        type: "info",
        text: "Use new credit responsibly and keep utilization below 30% to maintain good credit"
      });

      // Calculate impact on credit score
      let creditScoreImpact = "Neutral";
      if (newUtilization < 10) creditScoreImpact = "Positive (+10-20 points)";
      else if (newUtilization < 30) creditScoreImpact = "Slightly Positive (+5-10 points)";
      else if (newUtilization > 70) creditScoreImpact = "Negative (-10-20 points)";
      else if (newUtilization > 50) creditScoreImpact = "Slightly Negative (-5-10 points)";

      return {
        name: card.name,
        currentUtilization: currentUtilization.toFixed(2),
        newUtilization: newUtilization.toFixed(2),
        newUtilizationRecommended: newUtilizationRecommended.toFixed(2),
        requestedIncrease: requestedIncrease.toFixed(2),
        recommendedIncrease: maxRecommendedIncrease.toFixed(2),
        approvalLikelihood: likelihood,
        approvalScore: approvalScore,
        likelihoodScore: likelihoodScore,
        recommendations,
        creditScoreImpact,
        dtiRatio: dtiRatio.toFixed(2),
        housingRatio: housingRatio.toFixed(2),
        approvalFactors: {
          utilization: currentUtilization < 30,
          creditScore: creditScore >= 700,
          paymentHistory: paymentHistory >= 12,
          timeSinceIncrease: lastIncrease >= 6,
          dtiRatio: dtiRatio < 40
        }
      };
    });

    // Calculate scenario comparison
    calculateScenarios(validCards[0], calcResults[0]);

    setResults(calcResults);
  };

  const calculateScenarios = (primaryCard, currentResult) => {
    if (!primaryCard?.limit || !currentResult) return;

    const limit = parseFloat(primaryCard.limit);
    const scenarios = [
      { name: "Conservative", multiplier: 0.5 },
      { name: "Recommended", multiplier: 1 },
      { name: "Aggressive", multiplier: 1.5 },
      { name: "Maximum", multiplier: 2 }
    ];

    const scenarioResults = scenarios.map(scenario => {
      const increaseAmount = parseFloat(currentResult.recommendedIncrease) * scenario.multiplier;
      const newUtilization = (parseFloat(primaryCard.balance) / (limit + increaseAmount)) * 100;
      
      let likelihood = "Medium";
      if (scenario.multiplier <= 0.5) likelihood = "Very High";
      else if (scenario.multiplier <= 1) likelihood = "High";
      else if (scenario.multiplier <= 1.5) likelihood = "Medium";
      else likelihood = "Low";

      return {
        name: scenario.name,
        increaseAmount: increaseAmount.toFixed(2),
        newLimit: (limit + increaseAmount).toFixed(2),
        newUtilization: newUtilization.toFixed(2),
        approvalLikelihood: likelihood,
        multiplier: scenario.multiplier
      };
    });

    setScenarioComparison(scenarioResults);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const utilizationComparisonData = results && results[0] ? {
    labels: ['Current', 'After Requested Increase', 'After Recommended Increase'],
    datasets: [
      {
        label: 'Credit Utilization (%)',
        data: [
          parseFloat(results[0].currentUtilization),
          parseFloat(results[0].newUtilization),
          parseFloat(results[0].newUtilizationRecommended)
        ],
        backgroundColor: ['#A03B00', '#F06201', '#38a169'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const approvalFactorsData = results && results[0] ? {
    labels: ['Utilization <30%', 'Score ≥700', 'Payment History', 'Time Since Increase', 'DTI <40%'],
    datasets: [
      {
        label: 'Meets Criteria',
        data: Object.values(results[0].approvalFactors).map(factor => factor ? 100 : 0),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const scenarioComparisonData = scenarioComparison ? {
    labels: scenarioComparison.map(scenario => scenario.name),
    datasets: [
      {
        label: 'Increase Amount ($)',
        data: scenarioComparison.map(scenario => parseFloat(scenario.increaseAmount)),
        backgroundColor: '#FF8C42',
        borderColor: '#FF8C42',
        borderWidth: 1,
      },
      {
        label: 'New Utilization (%)',
        data: scenarioComparison.map(scenario => parseFloat(scenario.newUtilization)),
        backgroundColor: '#A03B00',
        borderColor: '#A03B00',
        borderWidth: 1,
      }
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const totalCurrentLimit = cards.reduce((sum, card) => sum + (parseFloat(card.limit) || 0), 0);
  const totalRequestedIncrease = results ? results.reduce((sum, result) => sum + parseFloat(result.requestedIncrease), 0) : 0;

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Credit Limit Increase Estimator</h1>
          <p>Calculate your approval chances and optimize your credit limit increase requests</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Current Limit:</span>
              <strong>${totalCurrentLimit.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Total Requested Increase:</span>
              <strong>${totalRequestedIncrease.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Cards:</span>
              <strong>{cards.length}</strong>
            </div>
            {results && results[0] && (
              <div className="summary-item">
                <span>Best Approval Chance:</span>
                <strong className={results[0].approvalLikelihood.includes('High') ? "savings" : ""}>
                  {results[0].approvalLikelihood}
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Credit Card Details</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  <i className="fa-solid fa-plus"></i> Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Maximize your approval chances by understanding issuer requirements and optimizing your request strategy
              </div>
            </div>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Current Credit Limit ($)*</label>
                      <input
                        type="number"
                        value={card.limit}
                        onChange={(e) => handleCardChange(card.id, "limit", e.target.value)}
                        placeholder="5000"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Current Balance ($)*</label>
                      <input
                        type="number"
                        value={card.balance}
                        onChange={(e) => handleCardChange(card.id, "balance", e.target.value)}
                        placeholder="1500"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Monthly Income ($)*</label>
                      <input
                        type="number"
                        value={card.income}
                        onChange={(e) => handleCardChange(card.id, "income", e.target.value)}
                        placeholder="6000"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Monthly Expenses ($)</label>
                      <input
                        type="number"
                        value={card.expenses}
                        onChange={(e) => handleCardChange(card.id, "expenses", e.target.value)}
                        placeholder="4500"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="input-group">
                      <label>Credit Score</label>
                      <input
                        type="number"
                        value={card.creditScore}
                        onChange={(e) => handleCardChange(card.id, "creditScore", e.target.value)}
                        placeholder="720"
                        min="300"
                        max="850"
                      />
                    </div>

                    <div className="input-group">
                      <label>On-time Payments (Months)</label>
                      <input
                        type="number"
                        value={card.paymentHistory}
                        onChange={(e) => handleCardChange(card.id, "paymentHistory", e.target.value)}
                        placeholder="24"
                        min="0"
                      />
                    </div>

                    <div className="input-group">
                      <label>Months Since Last Increase</label>
                      <input
                        type="number"
                        value={card.lastIncreaseMonths}
                        onChange={(e) => handleCardChange(card.id, "lastIncreaseMonths", e.target.value)}
                        placeholder="12"
                        min="0"
                      />
                    </div>

                    <div className="input-group">
                      <label>Account Age (Months)</label>
                      <input
                        type="number"
                        value={card.accountAge}
                        onChange={(e) => handleCardChange(card.id, "accountAge", e.target.value)}
                        placeholder="24"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Increase Request Section */}
                  <div className="increase-request-section">
                    <h4>Limit Increase Request</h4>
                    <div className="input-grid">
                      <div className="input-group">
                        <label>Request Type</label>
                        <select 
                          value={card.requestedAmountType} 
                          onChange={(e) => handleCardChange(card.id, "requestedAmountType", e.target.value)}
                        >
                          <option value="percentage">Percentage Increase</option>
                          <option value="amount">Specific Amount</option>
                        </select>
                      </div>

                      {card.requestedAmountType === "percentage" ? (
                        <div className="input-group">
                          <label>Percentage Increase (%)</label>
                          <input
                            type="number"
                            value={card.percentIncrease}
                            onChange={(e) => handleCardChange(card.id, "percentIncrease", e.target.value)}
                            placeholder="40"
                            min="0"
                            max="200"
                            step="1"
                          />
                        </div>
                      ) : (
                        <div className="input-group">
                          <label>Desired Increase Amount ($)</label>
                          <input
                            type="number"
                            value={card.desiredIncrease}
                            onChange={(e) => handleCardChange(card.id, "desiredIncrease", e.target.value)}
                            placeholder="2000"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      )}

                      <div className="input-group">
                        <label>Card Issuer</label>
                        <select 
                          value={card.issuer} 
                          onChange={(e) => handleCardChange(card.id, "issuer", e.target.value)}
                        >
                          <option value="Chase">Chase</option>
                          <option value="Bank of America">Bank of America</option>
                          <option value="Citi">Citi</option>
                          <option value="American Express">American Express</option>
                          <option value="Capital One">Capital One</option>
                          <option value="Discover">Discover</option>
                          <option value="Wells Fargo">Wells Fargo</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label>Total Open Accounts</label>
                        <input
                          type="number"
                          value={card.openAccounts}
                          onChange={(e) => handleCardChange(card.id, "openAccounts", e.target.value)}
                          placeholder="3"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="required-note">* Required fields</div>
                </div>
              ))}
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateEstimates}>
              Calculate Approval Chances
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Credit Limit Increase Guide</h2>
            <div className="default-message">
              <p>Enter your credit card details and financial information to estimate your approval chances for a credit limit increase.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Increase Approval Tips:</h3>
                <ul>
                  <li>Wait 6+ months between increase requests for best results</li>
                  <li>Keep credit utilization below 30% before requesting</li>
                  <li>Update your income information with the issuer</li>
                  <li>Make all payments on time for at least 6-12 months</li>
                  <li>Consider your debt-to-income ratio when requesting amounts</li>
                  <li>Different issuers have different policies - research your specific card</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results && results.length > 0 ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Limit Increase Analysis</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-chart-line"></i></div>
                  <h3>Approval Likelihood</h3>
                  <div className={`summary-value ${
                    results[0].approvalLikelihood.includes('High') ? "savings" : 
                    results[0].approvalLikelihood.includes('Low') ? "warning" : ""
                  }`}>
                    {results[0].approvalLikelihood}
                  </div>
                  <div className="summary-note">{results[0].likelihoodScore} chance</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-percentage"></i></div>
                  <h3>Current Utilization</h3>
                  <div className="summary-value">{results[0].currentUtilization}%</div>
                  <div className="summary-note">
                    {results[0].currentUtilization > 30 ? "High" : "Good"}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Recommended Increase</h3>
                  <div className="summary-value">${results[0].recommendedIncrease}</div>
                  <div className="summary-note">Optimal amount</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-credit-card"></i></div>
                  <h3>Credit Impact</h3>
                  <div className="summary-value">
                    {results[0].creditScoreImpact.includes('Positive') ? "Positive" : 
                     results[0].creditScoreImpact.includes('Negative') ? "Negative" : "Neutral"}
                  </div>
                  <div className="summary-note">Score change</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Utilization Impact</h3>
                    {utilizationComparisonData && <Bar data={utilizationComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Utilization Analysis:</strong> See how your credit utilization changes with different increase amounts.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Approval Factors</h3>
                    {approvalFactorsData && <Bar data={approvalFactorsData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Key Criteria:</strong> Track which approval factors you meet for optimal chances.</p>
                    </div>
                  </div>

                  {scenarioComparison && (
                    <div className="chart-card">
                      <h3>Request Strategy Comparison</h3>
                      <Bar data={scenarioComparisonData} options={chartOptions} />
                      <div className="chart-description">
                        <p><strong>Strategy Analysis:</strong> Compare different request amounts and their approval likelihood.</p>
                      </div>
                    </div>
                  )}

                  <div className="chart-card">
                    <h3>Approval Score Breakdown</h3>
                    <Doughnut 
                      data={{
                        labels: ['Utilization', 'Credit Score', 'Payment History', 'Time Since Increase', 'DTI Ratio'],
                        datasets: [{
                          data: [30, 25, 20, 15, 10],
                          backgroundColor: ['#F06201', '#FF8C42', '#A03B00', '#FFB088', '#FFD1B8'],
                          borderWidth: 2,
                          borderColor: '#fff',
                        }]
                      }} 
                      options={chartOptions} 
                    />
                    <div className="chart-description">
                      <p><strong>Scoring System:</strong> Understand how different factors contribute to your approval score.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.map((result, index) => (
                      <div key={index} className="result-card">
                        <h3>{result.name} - Increase Analysis</h3>
                        <div className="result-stats">
                          <div className="stat">
                            <label>Current Credit Limit</label>
                            <span className="stat-value">${parseFloat(cards[index].limit).toFixed(2)}</span>
                          </div>
                          <div className="stat">
                            <label>Current Balance</label>
                            <span className="stat-value">${parseFloat(cards[index].balance).toFixed(2)}</span>
                          </div>
                          <div className="stat">
                            <label>Current Utilization</label>
                            <span className={`stat-value ${result.currentUtilization > 30 ? "warning" : ""}`}>
                              {result.currentUtilization}%
                            </span>
                          </div>
                          <div className="stat">
                            <label>Requested Increase</label>
                            <span className="stat-value">${result.requestedIncrease}</span>
                          </div>
                          <div className="stat">
                            <label>Recommended Increase</label>
                            <span className="stat-value savings">${result.recommendedIncrease}</span>
                          </div>
                          <div className="stat">
                            <label>New Utilization (Recommended)</label>
                            <span className="stat-value">{result.newUtilizationRecommended}%</span>
                          </div>
                          <div className="stat">
                            <label>Approval Likelihood</label>
                            <span className={`stat-value ${
                              result.approvalLikelihood.includes('High') ? "savings" : 
                              result.approvalLikelihood.includes('Low') ? "warning" : ""
                            }`}>
                              {result.approvalLikelihood} ({result.likelihoodScore})
                            </span>
                          </div>
                          <div className="stat">
                            <label>Approval Score</label>
                            <span className="stat-value">{result.approvalScore}/100</span>
                          </div>
                          <div className="stat">
                            <label>Credit Score Impact</label>
                            <span className={`stat-value ${
                              result.creditScoreImpact.includes('Positive') ? "savings" : 
                              result.creditScoreImpact.includes('Negative') ? "warning" : ""
                            }`}>
                              {result.creditScoreImpact}
                            </span>
                          </div>
                          <div className="stat">
                            <label>Debt-to-Income Ratio</label>
                            <span className="stat-value">{result.dtiRatio}%</span>
                          </div>
                        </div>
                        
                        <div className="monthly-breakdown">
                          <h4>Key Approval Factors</h4>
                          <div className="breakdown-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Factor</th>
                                  <th>Status</th>
                                  <th>Importance</th>
                                  <th>Your Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>Credit Utilization</td>
                                  <td>{result.approvalFactors.utilization ? "✅ Meets" : "❌ Needs Improvement"}</td>
                                  <td>High (30%)</td>
                                  <td>{result.currentUtilization}%</td>
                                </tr>
                                <tr>
                                  <td>Credit Score</td>
                                  <td>{result.approvalFactors.creditScore ? "✅ Meets" : "❌ Needs Improvement"}</td>
                                  <td>High (25%)</td>
                                  <td>{cards[index].creditScore || "Not provided"}</td>
                                </tr>
                                <tr>
                                  <td>Payment History</td>
                                  <td>{result.approvalFactors.paymentHistory ? "✅ Meets" : "❌ Needs Improvement"}</td>
                                  <td>Medium (20%)</td>
                                  <td>{cards[index].paymentHistory || "0"} months</td>
                                </tr>
                                <tr>
                                  <td>Time Since Last Increase</td>
                                  <td>{result.approvalFactors.timeSinceIncrease ? "✅ Meets" : "❌ Needs Improvement"}</td>
                                  <td>Medium (15%)</td>
                                  <td>{cards[index].lastIncreaseMonths || "0"} months</td>
                                </tr>
                                <tr>
                                  <td>Debt-to-Income Ratio</td>
                                  <td>{result.approvalFactors.dtiRatio ? "✅ Meets" : "❌ Needs Improvement"}</td>
                                  <td>Low (10%)</td>
                                  <td>{result.dtiRatio}%</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="default-results">
                          <h4>Actionable Recommendations</h4>
                          <div className="default-message">
                            {result.recommendations.map((rec, i) => (
                              <div key={i} className={`default-tips ${rec.type}`}>
                                <i className={`fa-solid ${
                                  rec.type === 'critical' ? 'fa-exclamation-triangle' :
                                  rec.type === 'warning' ? 'fa-exclamation-circle' : 'fa-info-circle'
                                }`}></i>
                                <span>{rec.text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Scenario Comparison */}
                  {scenarioComparison && (
                    <div className="scenario-comparison">
                      <h3>Request Strategy Analysis</h3>
                      <div className="scenario-grid">
                        {scenarioComparison.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name} Approach</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>Increase Amount</label>
                                <span>${scenario.increaseAmount}</span>
                              </div>
                              <div className="stat">
                                <label>New Credit Limit</label>
                                <span>${scenario.newLimit}</span>
                              </div>
                              <div className="stat">
                                <label>New Utilization</label>
                                <span>{scenario.newUtilization}%</span>
                              </div>
                              <div className="stat">
                                <label>Approval Likelihood</label>
                                <span className={
                                  scenario.approvalLikelihood.includes('High') ? "savings" : 
                                  scenario.approvalLikelihood.includes('Low') ? "warning" : ""
                                }>
                                  {scenario.approvalLikelihood}
                                </span>
                              </div>
                              <div className="stat">
                                <label>Strategy</label>
                                <span>
                                  {scenario.multiplier <= 0.5 ? "Very conservative approach" :
                                   scenario.multiplier <= 1 ? "Balanced approach" :
                                   scenario.multiplier <= 1.5 ? "Moderately aggressive" :
                                   "Maximum possible request"}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your credit card details and financial information to analyze your limit increase approval chances.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CreditLimitIncreaseEstimator;